/*!
* @license
* Copyright 2018 Alfresco Software, Ltd.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*     http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/
import { SiteContainerEntry } from '../model/siteContainerEntry';
import { SiteContainerPaging } from '../model/siteContainerPaging';
import { SiteEntry } from '../model/siteEntry';
import { SiteMemberEntry } from '../model/siteMemberEntry';
import { SiteMemberPaging } from '../model/siteMemberPaging';
import { SiteMembershipRequestEntry } from '../model/siteMembershipRequestEntry';
import { SiteMembershipRequestPaging } from '../model/siteMembershipRequestPaging';
import { SiteMembershipRequestWithPersonPaging } from '../model/siteMembershipRequestWithPersonPaging';
import { SitePaging } from '../model/sitePaging';
import { SiteRoleEntry } from '../model/siteRoleEntry';
import { SiteRolePaging } from '../model/siteRolePaging';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { buildCollectionParam } from '../../../alfrescoApiClient';
import { SiteGroupEntry } from '../model/siteGroupEntry';
import { SiteGroupPaging } from '../model/siteGroupPaging';
export class SitesApi extends BaseApi {
    approveSiteMembershipRequest(siteId, inviteeId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(inviteeId, 'inviteeId');
        opts = opts || {};
        const postBody = opts['siteMembershipApprovalBody'];
        const pathParams = {
            'siteId': siteId, 'inviteeId': inviteeId
        };
        const queryParams = {};
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}/site-membership-requests/{inviteeId}/approve', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts);
    }
    createSite(siteBodyCreate, opts) {
        throwIfNotDefined(siteBodyCreate, 'siteBodyCreate');
        opts = opts || {};
        const postBody = siteBodyCreate;
        const pathParams = {};
        const queryParams = {
            'skipConfiguration': opts['skipConfiguration'],
            'skipAddToFavorites': opts['skipAddToFavorites'],
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteEntry);
    }
    createSiteMembership(siteId, siteMembershipBodyCreate, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(siteMembershipBodyCreate, 'siteMembershipBodyCreate');
        opts = opts || {};
        const postBody = siteMembershipBodyCreate;
        const pathParams = {
            'siteId': siteId
        };
        const queryParams = {
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}/members', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteMemberEntry);
    }
    createSiteMembershipRequestForPerson(personId, siteMembershipRequestBodyCreate, opts) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteMembershipRequestBodyCreate, 'siteMembershipRequestBodyCreate');
        opts = opts || {};
        const postBody = siteMembershipRequestBodyCreate;
        const pathParams = {
            'personId': personId
        };
        const queryParams = {
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/people/{personId}/site-membership-requests', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteMembershipRequestEntry);
    }
    deleteSite(siteId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'siteId': siteId
        };
        const queryParams = {
            'permanent': opts['permanent']
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}', 'DELETE', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts);
    }
    deleteSiteMembership(siteId, personId) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(personId, 'personId');
        const postBody = null;
        const pathParams = {
            'siteId': siteId, 'personId': personId
        };
        const queryParams = {};
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}/members/{personId}', 'DELETE', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts);
    }
    deleteSiteMembershipForPerson(personId, siteId) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteId, 'siteId');
        const postBody = null;
        const pathParams = {
            'personId': personId, 'siteId': siteId
        };
        const queryParams = {};
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/people/{personId}/sites/{siteId}', 'DELETE', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts);
    }
    deleteSiteMembershipRequestForPerson(personId, siteId) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteId, 'siteId');
        const postBody = null;
        const pathParams = {
            'personId': personId, 'siteId': siteId
        };
        const queryParams = {};
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/people/{personId}/site-membership-requests/{siteId}', 'DELETE', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts);
    }
    getSite(siteId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'siteId': siteId
        };
        const queryParams = {
            'relations': buildCollectionParam(opts['relations'], 'csv'),
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteEntry);
    }
    getSiteContainer(siteId, containerId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(containerId, 'containerId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'siteId': siteId, 'containerId': containerId
        };
        const queryParams = {
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}/containers/{containerId}', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteContainerEntry);
    }
    getSiteMembership(siteId, personId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(personId, 'personId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'siteId': siteId, 'personId': personId
        };
        const queryParams = {
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}/members/{personId}', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteMemberEntry);
    }
    getSiteMembershipForPerson(personId, siteId) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteId, 'siteId');
        const postBody = null;
        const pathParams = {
            'personId': personId, 'siteId': siteId
        };
        const queryParams = {};
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/people/{personId}/sites/{siteId}', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteRoleEntry);
    }
    getSiteMembershipRequestForPerson(personId, siteId, opts) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteId, 'siteId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'personId': personId, 'siteId': siteId
        };
        const queryParams = {
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/people/{personId}/site-membership-requests/{siteId}', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteMembershipRequestEntry);
    }
    getSiteMembershipRequests(opts) {
        opts = opts || {};
        const postBody = null;
        const pathParams = {};
        const queryParams = {
            'skipCount': opts['skipCount'],
            'maxItems': opts['maxItems'],
            'where': opts['where'],
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/site-membership-requests', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteMembershipRequestWithPersonPaging);
    }
    listSiteContainers(siteId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'siteId': siteId
        };
        const queryParams = {
            'skipCount': opts['skipCount'],
            'maxItems': opts['maxItems'],
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}/containers', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteContainerPaging);
    }
    listSiteMembershipRequestsForPerson(personId, opts) {
        throwIfNotDefined(personId, 'personId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'personId': personId
        };
        const queryParams = {
            'skipCount': opts['skipCount'],
            'maxItems': opts['maxItems'],
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/people/{personId}/site-membership-requests', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteMembershipRequestPaging);
    }
    listSiteMemberships(siteId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'siteId': siteId
        };
        const queryParams = {
            'skipCount': opts['skipCount'],
            'maxItems': opts['maxItems'],
            'where': opts['where'],
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}/members', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteMemberPaging);
    }
    listSiteMembershipsForPerson(personId, opts) {
        throwIfNotDefined(personId, 'personId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'personId': personId
        };
        const queryParams = {
            'skipCount': opts['skipCount'],
            'maxItems': opts['maxItems'],
            'orderBy': buildCollectionParam(opts['orderBy'], 'csv'),
            'relations': buildCollectionParam(opts['relations'], 'csv'),
            'fields': buildCollectionParam(opts['fields'], 'csv'),
            'where': opts['where']
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/people/{personId}/sites', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteRolePaging);
    }
    listSites(opts) {
        opts = opts || {};
        const postBody = null;
        const pathParams = {};
        const queryParams = {
            'skipCount': opts['skipCount'],
            'maxItems': opts['maxItems'],
            'orderBy': buildCollectionParam(opts['orderBy'], 'csv'),
            'relations': buildCollectionParam(opts['relations'], 'csv'),
            'fields': buildCollectionParam(opts['fields'], 'csv'),
            'where': opts['where']
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SitePaging);
    }
    rejectSiteMembershipRequest(siteId, inviteeId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(inviteeId, 'inviteeId');
        opts = opts || {};
        const postBody = opts['siteMembershipRejectionBody'];
        const pathParams = {
            'siteId': siteId, 'inviteeId': inviteeId
        };
        const queryParams = {};
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}/site-membership-requests/{inviteeId}/reject', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts);
    }
    updateSite(siteId, siteBodyUpdate, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(siteBodyUpdate, 'siteBodyUpdate');
        opts = opts || {};
        const postBody = siteBodyUpdate;
        const pathParams = {
            'siteId': siteId
        };
        const queryParams = {
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}', 'PUT', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteEntry);
    }
    updateSiteMembership(siteId, personId, siteMembershipBodyUpdate, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteMembershipBodyUpdate, 'siteMembershipBodyUpdate');
        opts = opts || {};
        const postBody = siteMembershipBodyUpdate;
        const pathParams = {
            'siteId': siteId, 'personId': personId
        };
        const queryParams = {
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}/members/{personId}', 'PUT', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteMemberEntry);
    }
    updateSiteMembershipRequestForPerson(personId, siteId, siteMembershipRequestBodyUpdate, opts) {
        throwIfNotDefined(personId, 'personId');
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(siteMembershipRequestBodyUpdate, 'siteMembershipRequestBodyUpdate');
        opts = opts || {};
        const postBody = siteMembershipRequestBodyUpdate;
        const pathParams = {
            'personId': personId, 'siteId': siteId
        };
        const queryParams = {
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/people/{personId}/site-membership-requests/{siteId}', 'PUT', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteMembershipRequestEntry);
    }
    createSiteGroupMembership(siteId, siteMembershipBodyCreate, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(siteMembershipBodyCreate, 'siteMembershipBodyCreate');
        opts = opts || {};
        const postBody = siteMembershipBodyCreate;
        const pathParams = {
            'siteId': siteId
        };
        const queryParams = {
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}/group-members', 'POST', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteGroupEntry);
    }
    listSiteGroups(siteId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'siteId': siteId
        };
        const queryParams = {
            'skipCount': opts['skipCount'],
            'maxItems': opts['maxItems'],
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}/group-members', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteGroupPaging);
    }
    getSiteGroupMembership(siteId, groupId, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(groupId, 'groupId');
        opts = opts || {};
        const postBody = null;
        const pathParams = {
            'siteId': siteId,
            'groupId': groupId
        };
        const queryParams = {
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}/group-members/{groupId}', 'GET', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteGroupEntry);
    }
    updateSiteGroupMembership(siteId, groupId, siteMembershipBodyUpdate, opts) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(groupId, 'groupId');
        throwIfNotDefined(siteMembershipBodyUpdate, 'siteMembershipBodyUpdate');
        opts = opts || {};
        const postBody = siteMembershipBodyUpdate;
        const pathParams = {
            'siteId': siteId,
            'groupId': groupId
        };
        const queryParams = {
            'fields': buildCollectionParam(opts['fields'], 'csv')
        };
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}/group-members/{groupId}', 'PUT', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts, SiteGroupEntry);
    }
    deleteSiteGroupMembership(siteId, groupId) {
        throwIfNotDefined(siteId, 'siteId');
        throwIfNotDefined(groupId, 'groupId');
        const postBody = null;
        const pathParams = {
            'siteId': siteId,
            'groupId': groupId
        };
        const queryParams = {};
        const headerParams = {};
        const formParams = {};
        const contentTypes = ['application/json'];
        const accepts = ['application/json'];
        return this.apiClient.callApi('/sites/{siteId}/group-members/{groupId}', 'DELETE', pathParams, queryParams, headerParams, formParams, postBody, contentTypes, accepts);
    }
}
